/**
 * Stat computation (the real Gen 3+ formula) + the 25 natures.
 *
 * Implemented now (not a stub) so the engine has a tested numeric core to build on.
 */
import type {
  BaseStats,
  IVs,
  EVs,
  ComputedStats,
  Nature,
  StatKey,
  NatureAffectedStat,
} from '@/shared';
import { STAT_KEYS } from '@/shared';

/** The 25 canonical natures (+stat / −stat). Neutral natures share raises===lowers. */
export const NATURES: readonly Nature[] = [
  { id: 'hardy', name: 'Hardy', raises: 'atk', lowers: 'atk' },
  { id: 'lonely', name: 'Lonely', raises: 'atk', lowers: 'def' },
  { id: 'brave', name: 'Brave', raises: 'atk', lowers: 'spe' },
  { id: 'adamant', name: 'Adamant', raises: 'atk', lowers: 'spatk' },
  { id: 'naughty', name: 'Naughty', raises: 'atk', lowers: 'spdef' },
  { id: 'bold', name: 'Bold', raises: 'def', lowers: 'atk' },
  { id: 'docile', name: 'Docile', raises: 'def', lowers: 'def' },
  { id: 'relaxed', name: 'Relaxed', raises: 'def', lowers: 'spe' },
  { id: 'impish', name: 'Impish', raises: 'def', lowers: 'spatk' },
  { id: 'lax', name: 'Lax', raises: 'def', lowers: 'spdef' },
  { id: 'timid', name: 'Timid', raises: 'spe', lowers: 'atk' },
  { id: 'hasty', name: 'Hasty', raises: 'spe', lowers: 'def' },
  { id: 'serious', name: 'Serious', raises: 'spe', lowers: 'spe' },
  { id: 'jolly', name: 'Jolly', raises: 'spe', lowers: 'spatk' },
  { id: 'naive', name: 'Naive', raises: 'spe', lowers: 'spdef' },
  { id: 'modest', name: 'Modest', raises: 'spatk', lowers: 'atk' },
  { id: 'mild', name: 'Mild', raises: 'spatk', lowers: 'def' },
  { id: 'quiet', name: 'Quiet', raises: 'spatk', lowers: 'spe' },
  { id: 'bashful', name: 'Bashful', raises: 'spatk', lowers: 'spatk' },
  { id: 'rash', name: 'Rash', raises: 'spatk', lowers: 'spdef' },
  { id: 'calm', name: 'Calm', raises: 'spdef', lowers: 'atk' },
  { id: 'gentle', name: 'Gentle', raises: 'spdef', lowers: 'def' },
  { id: 'sassy', name: 'Sassy', raises: 'spdef', lowers: 'spe' },
  { id: 'careful', name: 'Careful', raises: 'spdef', lowers: 'spatk' },
  { id: 'quirky', name: 'Quirky', raises: 'spdef', lowers: 'spdef' },
];

const NATURE_BY_ID = new Map(NATURES.map((n) => [n.id, n]));

/** Look up a nature by id; throws if unknown (data integrity). */
export function getNature(id: string): Nature {
  const n = NATURE_BY_ID.get(id);
  if (!n) throw new Error(`Unknown nature id: ${id}`);
  return n;
}

/** The nature multiplier (0.9 / 1.0 / 1.1) for a given non-HP stat. */
export function natureModifier(nature: Nature, stat: NatureAffectedStat): number {
  if (nature.raises === nature.lowers) return 1; // neutral nature
  if (stat === nature.raises) return 1.1;
  if (stat === nature.lowers) return 0.9;
  return 1;
}

function commonStat(base: number, iv: number, ev: number, level: number): number {
  return Math.floor(Math.floor((2 * base + iv + Math.floor(ev / 4)) * level) / 100);
}

/**
 * Compute a mon's full stats at a level.
 *
 *   HP    = floor((2*base + iv + floor(ev/4)) * level / 100) + level + 10
 *   other = floor((floor((2*base + iv + floor(ev/4)) * level / 100) + 5) * natureMod)
 */
export function computeStats(
  base: BaseStats,
  ivs: IVs,
  evs: EVs,
  level: number,
  nature: Nature,
): ComputedStats {
  const out = {} as Record<StatKey, number>;
  for (const key of STAT_KEYS) {
    const inner = commonStat(base[key], ivs[key], evs[key], level);
    if (key === 'hp') {
      out[key] = inner + level + 10;
    } else {
      const mod = natureModifier(nature, key as NatureAffectedStat);
      out[key] = Math.floor((inner + 5) * mod);
    }
  }
  return out;
}
